// Real SendGrid API Integration Service
class RealSendGridService {
  constructor() {
    this.apiKey = process.env.REACT_APP_SENDGRID_API_KEY;
    this.fromEmail = process.env.REACT_APP_SENDGRID_FROM_EMAIL || 'noreply@silvalogistics.com';
    this.baseURL = 'https://api.sendgrid.com/v3';
  }

  // Send email via SendGrid
  async sendEmail(to, subject, content, templateId = null) {
    try {
      const emailData = {
        personalizations: [{
          to: [{ email: to }],
          subject: subject
        }],
        from: { email: this.fromEmail, name: 'Silva Logistics' },
        content: [{
          type: 'text/html',
          value: content
        }]
      };

      if (templateId) {
        emailData.template_id = templateId;
      }

      const response = await fetch(`${this.baseURL}/mail/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(emailData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`SendGrid API error: ${response.status} - ${JSON.stringify(errorData)}`);
      }

      return {
        success: true,
        messageId: response.headers.get('X-Message-Id'),
        status: 'sent'
      };
    } catch (error) {
      console.error('Error sending email via SendGrid:', error);
      throw error;
    }
  }

  // Send shipment notification
  async sendShipmentNotification(userEmail, shipmentData) {
    const subject = `Envío Creado - ${shipmentData.trackingNumber}`;
    const content = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #2c3e50;">¡Tu envío ha sido creado!</h2>
        <p>Hola,</p>
        <p>Tu envío ha sido procesado exitosamente con los siguientes detalles:</p>
        
        <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="margin-top: 0;">Detalles del Envío</h3>
          <p><strong>Número de Seguimiento:</strong> ${shipmentData.trackingNumber}</p>
          <p><strong>Servicio:</strong> ${shipmentData.service}</p>
          <p><strong>Costo:</strong> $${shipmentData.cost}</p>
          <p><strong>Fecha Estimada de Entrega:</strong> ${shipmentData.estimatedDelivery}</p>
        </div>
        
        <div style="background-color: #e8f5e8; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="margin-top: 0;">Direcciones</h3>
          <p><strong>Origen:</strong><br>
          ${shipmentData.origin.name}<br>
          ${shipmentData.origin.address}<br>
          ${shipmentData.origin.city}, ${shipmentData.origin.state} ${shipmentData.origin.zipCode}</p>
          
          <p><strong>Destino:</strong><br>
          ${shipmentData.destination.name}<br>
          ${shipmentData.destination.address}<br>
          ${shipmentData.destination.city}, ${shipmentData.destination.state} ${shipmentData.destination.zipCode}</p>
        </div>
        
        <p>Puedes rastrear tu envío en tiempo real en nuestra plataforma.</p>
        <p>Gracias por confiar en Silva Logistics.</p>
      </div>
    `;

    return await this.sendEmail(userEmail, subject, content);
  }

  // Send tracking update notification
  async sendTrackingUpdate(userEmail, trackingData) {
    const subject = `Actualización de Seguimiento - ${trackingData.trackingNumber}`;
    const content = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #2c3e50;">Actualización de tu Envío</h2>
        <p>Hola,</p>
        <p>Tu envío ha sido actualizado:</p>
        
        <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="margin-top: 0;">Estado Actual</h3>
          <p><strong>Número de Seguimiento:</strong> ${trackingData.trackingNumber}</p>
          <p><strong>Estado:</strong> ${trackingData.status}</p>
          <p><strong>Ubicación:</strong> ${trackingData.events[0]?.location || 'En tránsito'}</p>
          <p><strong>Fecha:</strong> ${new Date().toLocaleDateString('es-MX')}</p>
        </div>
        
        <div style="background-color: #fff3cd; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="margin-top: 0;">Última Actividad</h3>
          <p>${trackingData.events[0]?.description || 'En tránsito hacia destino'}</p>
        </div>
        
        <p>Puedes ver el seguimiento completo en nuestra plataforma.</p>
        <p>Gracias por confiar en Silva Logistics.</p>
      </div>
    `;

    return await this.sendEmail(userEmail, subject, content);
  }

  // Send payment confirmation
  async sendPaymentConfirmation(userEmail, paymentData) {
    const subject = `Confirmación de Pago - ${paymentData.paymentIntentId}`;
    const content = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #27ae60;">¡Pago Confirmado!</h2>
        <p>Hola,</p>
        <p>Tu pago ha sido procesado exitosamente:</p>
        
        <div style="background-color: #e8f5e8; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="margin-top: 0;">Detalles del Pago</h3>
          <p><strong>ID de Transacción:</strong> ${paymentData.paymentIntentId}</p>
          <p><strong>Monto:</strong> $${paymentData.amount}</p>
          <p><strong>Moneda:</strong> ${paymentData.currency.toUpperCase()}</p>
          <p><strong>Estado:</strong> ${paymentData.status}</p>
          <p><strong>Fecha:</strong> ${new Date().toLocaleDateString('es-MX')}</p>
        </div>
        
        <p>Tu factura estará disponible en tu cuenta.</p>
        <p>Gracias por tu confianza en Silva Logistics.</p>
      </div>
    `;

    return await this.sendEmail(userEmail, subject, content);
  }

  // Send order confirmation
  async sendOrderConfirmation(userEmail, orderData) {
    const subject = `Confirmación de Pedido - ${orderData.orderNumber}`;
    const content = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #2c3e50;">¡Pedido Confirmado!</h2>
        <p>Hola,</p>
        <p>Tu pedido ha sido procesado exitosamente:</p>
        
        <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="margin-top: 0;">Detalles del Pedido</h3>
          <p><strong>Número de Pedido:</strong> ${orderData.orderNumber}</p>
          <p><strong>Total:</strong> $${orderData.totalPrice}</p>
          <p><strong>Estado:</strong> ${orderData.fulfillmentStatus}</p>
          <p><strong>Fecha:</strong> ${new Date(orderData.createdAt).toLocaleDateString('es-MX')}</p>
        </div>
        
        <div style="background-color: #e8f5e8; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="margin-top: 0;">Productos</h3>
          ${orderData.items.map(item => `
            <p><strong>${item.title}</strong> - Cantidad: ${item.quantity} - $${item.price}</p>
          `).join('')}
        </div>
        
        <p>Te notificaremos cuando tu pedido esté listo para envío.</p>
        <p>Gracias por tu compra.</p>
      </div>
    `;

    return await this.sendEmail(userEmail, subject, content);
  }

  // Send password reset
  async sendPasswordReset(userEmail, resetToken) {
    const subject = 'Restablecer Contraseña - Silva Logistics';
    const resetUrl = `${window.location.origin}/reset-password?token=${resetToken}`;
    const content = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #2c3e50;">Restablecer Contraseña</h2>
        <p>Hola,</p>
        <p>Has solicitado restablecer tu contraseña. Haz clic en el botón de abajo para continuar:</p>
        
        <div style="text-align: center; margin: 30px 0;">
          <a href="${resetUrl}" style="background-color: #3498db; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block;">
            Restablecer Contraseña
          </a>
        </div>
        
        <p>Si no solicitaste este cambio, puedes ignorar este correo.</p>
        <p>Este enlace expirará en 1 hora.</p>
        <p>Gracias,<br>Equipo de Silva Logistics</p>
      </div>
    `;

    return await this.sendEmail(userEmail, subject, content);
  }

  // Send welcome email
  async sendWelcomeEmail(userEmail, userName) {
    const subject = '¡Bienvenido a Silva Logistics!';
    const content = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #2c3e50;">¡Bienvenido a Silva Logistics!</h2>
        <p>Hola ${userName},</p>
        <p>¡Gracias por registrarte en Silva Logistics! Estamos emocionados de tenerte como parte de nuestra comunidad.</p>
        
        <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="margin-top: 0;">¿Qué puedes hacer?</h3>
          <ul>
            <li>Crear envíos con las mejores tarifas</li>
            <li>Rastrear tus paquetes en tiempo real</li>
            <li>Gestionar pagos de forma segura</li>
            <li>Integrar con tu tienda online</li>
            <li>Recibir notificaciones automáticas</li>
          </ul>
        </div>
        
        <p>Si tienes alguna pregunta, no dudes en contactarnos.</p>
        <p>¡Bienvenido al futuro de la logística!</p>
        <p>Equipo de Silva Logistics</p>
      </div>
    `;

    return await this.sendEmail(userEmail, subject, content);
  }

  // Send bulk email
  async sendBulkEmail(recipients, subject, content, templateId = null) {
    try {
      const emailData = {
        personalizations: recipients.map(recipient => ({
          to: [{ email: recipient.email }],
          subject: subject,
          substitutions: recipient.substitutions || {}
        })),
        from: { email: this.fromEmail, name: 'Silva Logistics' },
        content: [{
          type: 'text/html',
          value: content
        }]
      };

      if (templateId) {
        emailData.template_id = templateId;
      }

      const response = await fetch(`${this.baseURL}/mail/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(emailData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`SendGrid API error: ${response.status} - ${JSON.stringify(errorData)}`);
      }

      return {
        success: true,
        messageIds: response.headers.get('X-Message-Id'),
        status: 'sent',
        recipientsCount: recipients.length
      };
    } catch (error) {
      console.error('Error sending bulk email via SendGrid:', error);
      throw error;
    }
  }

  // Demo email fallback
  async sendDemoEmail(to, subject, content) {
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log('Demo email sent:', { to, subject, content });
        resolve({
          success: true,
          messageId: `demo_${Date.now()}`,
          status: 'sent'
        });
      }, 1000);
    });
  }
}

// Create singleton instance
const realSendGridService = new RealSendGridService();

export default realSendGridService; 